import React, { useState } from 'react';
import { ChevronLeft, Sparkles, Lock, RefreshCw } from 'lucide-react';
interface Appearance {
  hairstyle: string;
  skinTone: string;
  eyeColor: string;
  outfitStyle: string;
}
interface Step6AppearanceProps {
  appearance: Appearance;
  onUpdate: (appearance: Appearance) => void;
  onComplete: () => void;
  onBack: () => void;
}
export const Step6Appearance: React.FC<Step6AppearanceProps> = ({
  appearance,
  onUpdate,
  onComplete,
  onBack
}) => {
  const [currentAppearance, setCurrentAppearance] = useState<Appearance>(appearance);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const hairstyles = [{
    id: 'long_straight',
    name: 'Long Straight'
  }, {
    id: 'long_curly',
    name: 'Long Curly'
  }, {
    id: 'medium_wavy',
    name: 'Medium Wavy'
  }, {
    id: 'short_bob',
    name: 'Short Bob'
  }, {
    id: 'pixie_cut',
    name: 'Pixie Cut'
  }, {
    id: 'braided',
    name: 'Braided'
  }, {
    id: 'ponytail',
    name: 'Ponytail'
  }];
  const skinTones = [{
    id: 'fair',
    name: 'Fair'
  }, {
    id: 'light',
    name: 'Light'
  }, {
    id: 'medium',
    name: 'Medium'
  }, {
    id: 'tan',
    name: 'Tan'
  }, {
    id: 'deep',
    name: 'Deep'
  }];
  const eyeColors = [{
    id: 'blue',
    name: 'Blue'
  }, {
    id: 'green',
    name: 'Green'
  }, {
    id: 'brown',
    name: 'Brown'
  }, {
    id: 'hazel',
    name: 'Hazel'
  }, {
    id: 'amber',
    name: 'Amber'
  }, {
    id: 'gray',
    name: 'Gray'
  }];
  const outfitStyles = [{
    id: 'casual_babe',
    name: 'Casual Babe',
    isPremium: false
  }, {
    id: 'business_formal',
    name: 'Business Formal',
    isPremium: false
  }, {
    id: 'sporty',
    name: 'Sporty',
    isPremium: false
  }, {
    id: 'punk_slut',
    name: 'Punk Slut',
    isPremium: false
  }, {
    id: 'naughty_nurse',
    name: 'Naughty Nurse',
    isPremium: true
  }, {
    id: 'latex_domme',
    name: 'Latex Domme',
    isPremium: true
  }, {
    id: 'schoolgirl',
    name: 'Schoolgirl',
    isPremium: false
  }, {
    id: 'goth',
    name: 'Goth',
    isPremium: false
  }];
  const handleChange = (field: keyof Appearance, value: string) => {
    const updatedAppearance = {
      ...currentAppearance,
      [field]: value
    };
    setCurrentAppearance(updatedAppearance);
    onUpdate(updatedAppearance);
  };
  const generatePreview = () => {
    setIsGeneratingPreview(true);
    // Simulate AI image generation
    setTimeout(() => {
      setIsGeneratingPreview(false);
      setPreviewGenerated(true);
    }, 3000);
  };
  const changeFace = () => {
    // Simulate changing face
    setIsGeneratingPreview(true);
    setTimeout(() => {
      setIsGeneratingPreview(false);
      // Face is already changed
    }, 1500);
  };
  const isComplete = currentAppearance.hairstyle && currentAppearance.skinTone && currentAppearance.eyeColor && currentAppearance.outfitStyle;
  return <div className="space-y-6" data-id="element-22223">
      <div className="flex items-center mb-6" data-id="element-22224">
        <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-22225">
          <ChevronLeft size={20} className="text-gray-500" data-id="element-22226" />
        </button>
        <div className="ml-4" data-id="element-22227">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-22228">
            Appearance & Style
          </h2>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-22229">
            Customize how your Angel looks
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-22230">
        <div className="space-y-6" data-id="element-22231">
          <div className="space-y-2" data-id="element-22232">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-22233">
              Hairstyle
            </label>
            <select value={currentAppearance.hairstyle} onChange={e => handleChange('hairstyle', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent" data-id="element-22234">
              <option value="" data-id="element-22235">Select a hairstyle</option>
              {hairstyles.map(style => <option key={style.id} value={style.id} data-id="element-22236">
                  {style.name}
                </option>)}
            </select>
          </div>
          <div className="space-y-2" data-id="element-22237">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-22238">
              Skin Tone
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-22239">
              {skinTones.map(tone => <button key={tone.id} onClick={() => handleChange('skinTone', tone.id)} className={`px-3 py-2 rounded-lg border transition-all ${currentAppearance.skinTone === tone.id ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-pink-300'}`} data-id="element-22240">
                  {tone.name}
                </button>)}
            </div>
          </div>
          <div className="space-y-2" data-id="element-22241">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-22242">
              Eye Color
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-22243">
              {eyeColors.map(color => <button key={color.id} onClick={() => handleChange('eyeColor', color.id)} className={`px-3 py-2 rounded-lg border transition-all ${currentAppearance.eyeColor === color.id ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-pink-300'}`} data-id="element-22244">
                  {color.name}
                </button>)}
            </div>
          </div>
          <div className="space-y-2" data-id="element-22245">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-22246">
              Outfit Style
            </label>
            <div className="grid grid-cols-2 gap-2" data-id="element-22247">
              {outfitStyles.map(style => <button key={style.id} onClick={() => !style.isPremium && handleChange('outfitStyle', style.id)} className={`px-3 py-2 rounded-lg border transition-all relative ${currentAppearance.outfitStyle === style.id ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-pink-300'} ${style.isPremium ? 'opacity-80' : ''}`} data-id="element-22248">
                  {style.name}
                  {style.isPremium && <div className="absolute top-1 right-1 text-pink-500" data-id="element-22249">
                      <Lock size={12} data-id="element-22250" />
                    </div>}
                </button>)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6" data-id="element-22251">
          {previewGenerated ? <div className="text-center" data-id="element-22252">
              <div className="w-48 h-48 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-5xl" data-id="element-22253">
                👩
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3" data-id="element-22254">
                Your Angel preview (this is a placeholder - the real app would
                show an AI-generated image)
              </p>
              <button onClick={changeFace} className="px-4 py-2 rounded-lg text-sm font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-500 hover:bg-pink-200 dark:hover:bg-pink-900/50 flex items-center gap-1 mx-auto" data-id="element-22255">
                <RefreshCw size={14} data-id="element-22256" />
                Change Face (1 LuvGem)
              </button>
            </div> : <div className="text-center" data-id="element-22257">
              <div className="w-48 h-48 bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-4 flex items-center justify-center" data-id="element-22258">
                {isGeneratingPreview ? <div className="animate-spin text-pink-500" data-id="element-22259">
                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" data-id="element-22260">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-22261"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-22262"></path>
                    </svg>
                  </div> : <Sparkles size={48} className="text-gray-400 dark:text-gray-600" data-id="element-22263" />}
              </div>
              <button onClick={generatePreview} disabled={!isComplete || isGeneratingPreview} className={`px-4 py-2 rounded-lg text-sm font-medium ${!isComplete || isGeneratingPreview ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-pink-100 dark:bg-pink-900/30 text-pink-500 hover:bg-pink-200 dark:hover:bg-pink-900/50'}`} data-id="element-22264">
                <div className="flex items-center gap-1" data-id="element-22265">
                  <Sparkles size={14} data-id="element-22266" />
                  {isGeneratingPreview ? 'Generating...' : 'Generate AI Preview'}
                </div>
              </button>
            </div>}
        </div>
      </div>
      <div className="pt-6" data-id="element-22267">
        <button onClick={onComplete} disabled={!isComplete} className={`w-full py-3 rounded-lg font-medium text-white ${!isComplete ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:shadow-lg hover:shadow-pink-500/30 transition-all'}`} data-id="element-22268">
          Unleash My Angel (10 LuvGem)
        </button>
      </div>
    </div>;
};